import { CardNumberElement, CardMonthElement, CardYearElement, CardCvvElement } from '@recurly/react-recurly';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Select, Switch } from '@trackman/web-shared-components';
import { useServices } from 'index';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscountData, PricingFormStateNew } from 'types/purchaseMembership';
import { isValidEmail } from 'utilities/inputValidation';

type Props = {
  pricingFormState: PricingFormStateNew;
  processingPayment: boolean;
  isButtonEnabled: boolean;
  isSubscriptionInCart: boolean;
  autoRenew: boolean;
  setPricingFormState: (state: PricingFormStateNew) => void;
  setDiscount: (discount: DiscountData) => void;
  handleAutoRenew: (event) => void;
};

export const PaymentDetails = ({
  pricingFormState,
  processingPayment,
  isButtonEnabled,
  isSubscriptionInCart,
  autoRenew,
  setPricingFormState,
  setDiscount,
  handleAutoRenew,
}: Props) => {
  const { t } = useTranslation();

  const profileService = useServices().profileService;
  const { data: signUpLists, isPending } = useQuery({
    queryKey: ['signUpLists'],
    queryFn: () => profileService.getSignUpLists(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const handleInputChange = (field: string, value: string) => {
    setPricingFormState({
      ...pricingFormState,
      [field]: value,
    });
  };

  const handleCountryInputChange = (value: string) => {
    setPricingFormState({
      ...pricingFormState,
      country: value,
      state: value === 'CA' ? 'Default' : '',
    });
  };

  return (
    <>
      <div>
        <span>{t('BillingInformation', { ns: 'webshop' })}</span>
        <Input
          label={t('VAT', { ns: 'webshop' })}
          inputProps={{ 'data-recurly': 'vat_number' }}
          onChange={(e) => handleInputChange('billingVatNumber', e.target.value)}
        />
        <EmailInput value={pricingFormState.email} updateEmail={(email) => handleInputChange('email', email)} />
        <Input
          label={t('FirstName', { ns: 'webshop' })}
          required
          inputProps={{ 'data-recurly': 'first_name' }}
          value={pricingFormState.first_name}
          onChange={(e) => handleInputChange('first_name', e.target.value)}
        />
        <Input
          label={t('LastName', { ns: 'webshop' })}
          required
          inputProps={{ 'data-recurly': 'last_name' }}
          value={pricingFormState.last_name}
          onChange={(e) => handleInputChange('last_name', e.target.value)}
        />
        <Input
          label={t('StreetName1', { ns: 'webshop' })}
          required
          inputProps={{ 'data-recurly': 'address1' }}
          value={pricingFormState.address1}
          onChange={(e) => handleInputChange('address1', e.target.value)}
        />
        <Input
          label={t('StreetName2', { ns: 'webshop' })}
          inputProps={{ 'data-recurly': 'address2' }}
          value={pricingFormState.address2}
          onChange={(e) => handleInputChange('address2', e.target.value)}
        />
        <Input
          label={t('PostalCode', { ns: 'webshop' })}
          required
          inputProps={{ 'data-recurly': 'postal_code' }}
          value={pricingFormState.postal_code}
          onChange={(e) => handleInputChange('postal_code', e.target.value)}
        />
        <Input
          label={t('City', { ns: 'webshop' })}
          required
          inputProps={{ 'data-recurly': 'city' }}
          value={pricingFormState.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
        />
        <Select
          label={t('Country', { ns: 'webshop' })}
          required
          inputProps={{ 'data-recurly': 'country' }}
          value={pricingFormState.country}
          onChange={(e) => handleCountryInputChange(e.target.value as string)}
          selectOptions={signUpLists?.Countries.map((country) => ({
            label: country.name === ' ' ? t('PleaseSelectACountry', { ns: 'webshop' }) : country.name,
            value: country.twoLetterIsoCode,
          }))}
        />
        {pricingFormState.country === 'US' ? (
          <StateInput
            stateCodes={signUpLists?.States}
            selectedState={pricingFormState.state}
            updateState={(state) => handleInputChange('state', state)}
          />
        ) : null}
        <div className='discount-code-input'>
          <span>{t('Discount', { ns: 'webshop' })}</span>
          <Input
            label={t('DiscountCode', { ns: 'webshop' })}
            inputProps={{ 'data-recurly': 'coupon' }}
            onChange={(e) => {
              handleInputChange('coupon', e.target.value);
              setDiscount({ couponCode: e.target.value, discount: '', currencySymbol: '' });
            }}
          />
        </div>
      </div>
      <div>
        <span>{t('CardInformation', { ns: 'webshop' })}</span>
        <CardNumberInput />
        <div className='expiry-date'>
          <ExpiryDateMonthInput />
          <ExpiryDateYearInput />
        </div>
        <CVVInput />
        {isSubscriptionInCart && (
          <div className='auto-renew'>
            <Switch label='' value={autoRenew} onChange={(e) => handleAutoRenew(e)} />
            <div className='auto-renew-label'>
              <div>{t('AutoRenew', { ns: 'webshop' })}</div>
              <div>{t('AutoRenewDescription', { ns: 'webshop' })}</div>
            </div>
          </div>
        )}
        <Button
          label={t('PlaceOrder', { ns: 'webshop' })}
          intent='primary'
          type='submit'
          className='place-order-button'
          disabled={!isButtonEnabled}
          isLoading={processingPayment}
        />
      </div>
    </>
  );
};

const EmailInput = ({ value, updateEmail }: { value: string; updateEmail: (email: string) => void }) => {
  const { t } = useTranslation();

  const [email, setEmail] = useState(value);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const handleIsEmailValid = (event) => {
    if (isValidEmail(event.target.value) || event.target.value === '') {
      setIsEmailValid(true);
    } else {
      setIsEmailValid(false);
    }
    updateEmail(event.target.value);
  };

  return (
    <Input
      label={t('Email', { ns: 'webshop' })}
      inputProps={{ 'data-recurly': 'email' }}
      required
      value={email}
      error={!isEmailValid}
      onChange={(e) => setEmail(e.target.value)}
      onBlur={handleIsEmailValid}
    />
  );
};

const StateInput = ({
  stateCodes,
  selectedState,
  updateState,
}: {
  stateCodes?: string[];
  selectedState: string;
  updateState: (state: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Select
      label={t('State', { ns: 'webshop' })}
      value={selectedState}
      inputProps={{ 'data-recurly': 'state' }}
      required
      onChange={(e) => updateState(e.target.value as string)}
      selectOptions={
        stateCodes?.map((state) => ({
          label: state === ' ' ? t('PleaseSelectAState', { ns: 'webshop' }) : state,
          value: state,
        })) || []
      }
    />
  );
};

const CardNumberInput = () => {
  const { t } = useTranslation();

  return (
    <div className='recurly-component-wrapper'>
      <CardNumberElement style={{ fontSize: '13px', padding: '0' }} />
      <span>{t('CardNumber', { ns: 'webshop' })}</span>
    </div>
  );
};

const ExpiryDateMonthInput = () => {
  const { t } = useTranslation();

  return (
    <div className='recurly-component-wrapper'>
      <CardMonthElement style={{ fontSize: '13px', padding: '0' }} />
      <span>{t('MM', { ns: 'webshop' })}</span>
    </div>
  );
};

const ExpiryDateYearInput = () => {
  const { t } = useTranslation();

  return (
    <div className='recurly-component-wrapper'>
      <CardYearElement style={{ fontSize: '13px', padding: '0' }} />
      <span>{t('YY', { ns: 'webshop' })}</span>
    </div>
  );
};

const CVVInput = () => {
  const { t } = useTranslation();

  return (
    <div className='recurly-component-wrapper'>
      <CardCvvElement style={{ fontSize: '13px', padding: '0' }} />
      <span>{t('Cvv', { ns: 'webshop' })}</span>
    </div>
  );
};
